// DailySummary.js
// This is the Daily Summary Class for the Sampling Event Form
import { v4 as uuidv4 } from "uuid";

export class DailySummary {
  constructor(data = {}) {
    this.id = data.id || uuidv4();
    this.samplingEventId = data.sampling_event_id || "";
    this.date = data.date || null;
    this.employees = data.employees || [];
    this.notes = data.notes || "";
    this.maintenance = data.maintenance || "";
    this.formsFilledOut = data.forms_filled_out || false;
    this.coolersPackedShipped = data.coolers_packed_shipped || false;
    this.siteSecure = data.site_secure || false;
    this.teamLeaderSignatureEmployeeId =
      data.team_leader_signature_employee_id || null;
    this.teamLeaderSignatureName = data.team_leader_signature_name || null;
    this.teamLeaderSignature = data.team_leader_signature
      ? "data:image/png;base64," + data.team_leader_signature
      : "";
    this.createdAt = data.created_at || null;
    this.updatedAt = data.updated_at || null;
  }

  _formatDate(date) {
    if (!date) return null;
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  setDate(date) {
    if (!date) {
      this.date = null;
    } else {
      this.date = this._formatDate(date);
    }
  }

  addEmployee(employee) {
    this.employees.push(employee);
  }

  removeEmployee(employee) {
    this.employees = this.employees.filter((e) => e !== employee);
  }

  setNotes(notes) {
    if (!notes) {
      this.notes = "";
    } else {
      this.notes = notes;
    }
  }

  setMaintenance(maintenance) {
    if (!maintenance) {
      this.maintenance = "";
    } else {
      this.maintenance = maintenance;
    }
  }

  setFormsFilledOut(formsFilledOut) {
    if (!formsFilledOut) {
      this.formsFilledOut = false;
    } else {
      this.formsFilledOut = formsFilledOut;
    }
  }

  setCoolersPackedShipped(coolersPackedShipped) {
    if (!coolersPackedShipped) {
      this.coolersPackedShipped = false;
    } else {
      this.coolersPackedShipped = coolersPackedShipped;
    }
  }

  setSiteSecure(siteSecure) {
    if (!siteSecure) {
      this.siteSecure = false;
    } else {
      this.siteSecure = siteSecure;
    }
  }

  setTeamLeaderSignatureEmployeeId(teamLeaderSignatureEmployeeId) {
    if (!teamLeaderSignatureEmployeeId) {
      this.teamLeaderSignatureEmployeeId = null;
    } else {
      this.teamLeaderSignatureEmployeeId = teamLeaderSignatureEmployeeId;
    }
  }

  setTeamLeaderSignatureName(teamLeaderSignatureName) {
    if (!teamLeaderSignatureName) {
      this.teamLeaderSignatureName = null;
    } else {
      this.teamLeaderSignatureName = teamLeaderSignatureName;
    }
  }

  setTeamLeaderSignature(teamLeaderSignature) {
    if (!teamLeaderSignature) {
      this.teamLeaderSignature = "";
    } else {
      if (teamLeaderSignature.startsWith("data:image/png;base64,")) {
        this.teamLeaderSignature = teamLeaderSignature;
      } else {
        this.teamLeaderSignature =
          "data:image/png;base64," + teamLeaderSignature;
      }
    }
  }

  // To json for sending to the database
  toJson() {
    return {
      id: this.id,
      sampling_event_id: this.samplingEventId,
      date: this.date,
      employees: this.employees,
      notes: this.notes,
      maintenance: this.maintenance,
      forms_filled_out: this.formsFilledOut,
      coolers_packed_shipped: this.coolersPackedShipped,
      site_secure: this.siteSecure,
      team_leader_signature_employee_id: this.teamLeaderSignatureEmployeeId,
      team_leader_signature_name: this.teamLeaderSignatureName,
      team_leader_signature:
        this.teamLeaderSignature.length > 0
          ? this.teamLeaderSignature.replace("data:image/png;base64,", "")
          : "",
      created_at: this.createdAt,
      updated_at: this.updatedAt,
    };
  }

  toPatch(action, fields) {
    const fieldMap = {
      id: "id",
      samplingEventId: "sampling_event_id",
      date: "date",
      employees: "employees",
      notes: "notes",
      maintenance: "maintenance",
      formsFilledOut: "forms_filled_out",
      coolersPackedShipped: "coolers_packed_shipped",
      siteSecure: "site_secure",
      teamLeaderSignatureEmployeeId: "team_leader_signature_employee_id",
      teamLeaderSignature: "team_leader_signature",
    };
    const patch = {
      patch_type: "sampling_event_daily_summary",
      action: action,
      id: this.id,
    };
    for (const [key, value] of Object.entries(fields)) {
      const dbField = fieldMap[key];
      if (dbField) {
        patch[dbField] = value;
      }
    }
    return patch;
  }
}
